import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const bannerSearchSchema = z.object({
  keyword: z.string().optional(),
  province: z.array(z.string()).optional(),
  area: z.array(z.string()).optional(),
  region: z.array(z.string()).optional(),
  territory: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export type BannerSearch = z.infer<typeof bannerSearchSchema>

export async function getBanners(search: BannerSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.banners.$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  const banners = await res.json()
  return banners
}

export async function getBannerDetails(id: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.banners[':id'].$get({ param: { id: String(id) } }, { init: { signal } })

  const bannerDetails = await res.json()
  return bannerDetails
}

export async function getValidateBanner(banner: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.assortment.banners.validate.$get(
    { query: { banner } },
    { init: { signal } },
  )

  const validateBanner = await res.json()
  return validateBanner
}

export async function connectBanner({
  sapErp,
  provinces,
}: {
  sapErp: string | null | undefined
  provinces: string[]
}) {
  if (!sapErp || provinces.length === 0) {
    throw new Error('Please provide a valid ERP and select at least one province.')
  }

  const api = getApi()
  const res = await api.assortment.banners.connect.$post({
    json: {
      sapErp,
      provinces,
    },
  })

  const message = await res.json()
  return message
}

export function useBanners(queries: BannerSearch) {
  return useQuery({
    queryKey: ['banners', queries],
    queryFn: ({ signal }) => getBanners(queries, signal),
  })
}

export function useBannerDetails(id: string) {
  return useQuery({
    queryKey: ['bannerDetails', { id }],
    queryFn: ({ signal }) => getBannerDetails(id, signal),
  })
}

export function useValidateBanner(banner: string, shouldValidate: boolean) {
  return useQuery({
    queryKey: ['validateBanner', { banner }],
    queryFn: ({ signal }) => getValidateBanner(banner, signal),
    enabled: shouldValidate,
  })
}

export function useConnectBanner(
  sapErp: string | null | undefined,
  setIsOpen: (isOpen: boolean) => void,
) {
  const { t } = useTranslation(['banners', 'common'])

  return useMutation({
    mutationKey: ['connectBanner', { sapErp }],
    mutationFn: connectBanner,
    onSuccess: (data) => {
      toast.success(t('toast.success', { ns: 'common' }), {
        description: data.message,
      })
      setIsOpen(false)
    },
    onError: (error) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: error.message || t('toast.connectBanner.error'),
      })
    },
  })
}
